import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { portfolioAPI } from '../services/api';
import { useHideAmounts, MASK } from '../context/HideAmountsContext';
import { renderCountryWithFlag } from '../utils/countryUtils';
import SharedTooltip from './SharedTooltip';
import './Allocations.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Group holdings by a key (e.g. 'sector', 'country') and aggregate P&L. */
function groupPerformance(holdings, key) {
  const groups = {};
  for (const h of holdings) {
    const name = h[key];
    if (!name) continue;
    if (!groups[name]) groups[name] = { value: 0, profit: 0, fxPnl: 0, count: 0 };
    groups[name].value  += h.market_value || 0;
    groups[name].profit += h.profit       || 0;
    groups[name].fxPnl  += h.fx_ppl       || 0;
    groups[name].count  += 1;
  }
  const result = {};
  for (const [name, g] of Object.entries(groups)) {
    const cost = g.value - g.profit;
    result[name] = {
      pnl:       g.profit,
      fxPnl:     g.fxPnl,
      returnPct: cost > 0 ? (g.profit / cost) * 100 : null,
      count:     g.count,
    };
  }
  return result;
}

/**
 * Aggregate holdings by tag: weight vs soft cap plus P&L. Caps come from
 * config.py TAG_CLUSTER_SOFT_CAPS via /api/portfolio/allocations — tags
 * overlap by design, so rows do not sum to 100%.
 */
function groupByTag(holdings, caps) {
  const total = holdings.reduce((s, h) => s + (h.market_value || 0), 0);
  const groups = {};
  for (const h of holdings) {
    for (const tag of h.tags || []) {
      if (!groups[tag]) groups[tag] = { value: 0, profit: 0, count: 0 };
      groups[tag].value  += h.market_value || 0;
      groups[tag].profit += h.profit       || 0;
      groups[tag].count  += 1;
    }
  }
  return Object.entries(groups)
    .map(([tag, g]) => {
      const cost = g.value - g.profit;
      return {
        tag,
        weightPct: total > 0 ? (g.value / total) * 100 : 0,
        capPct: caps[tag] ?? null,
        returnPct: cost > 0 ? (g.profit / cost) * 100 : null,
        pnl: g.profit,
        count: g.count,
      };
    })
    .sort((a, b) => b.weightPct - a.weightPct);
}

const fmtPct  = (v)  => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
// Math.abs strips the sign, so losses must get an explicit one — without it a
// −£1,111 renders as "£1,111" and reads as a gain to anyone not seeing colour.
const fmtPnl  = (v)  => v == null ? '—' : `${v >= 0 ? '+' : '−'}£${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtGBP  = (v)  => v == null ? '—' : `£${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// ── Subcomponents ─────────────────────────────────────────────────────────────

const SummaryCard = ({ label, name, returnPct, pnl, hideAmounts }) => {
  const positive = returnPct == null || returnPct >= 0;
  return (
    <div className={`alloc-summary-card ${positive ? 'positive' : 'negative'}`}>
      <div className="alloc-summary-label">{label}</div>
      <div className="alloc-summary-name">{name ?? '—'}</div>
      <div className="alloc-summary-return">{fmtPct(returnPct)}</div>
      {pnl != null && <div className="alloc-summary-pnl">{hideAmounts ? MASK : fmtPnl(pnl)}</div>}
    </div>
  );
};

const SortToggle = ({ value, onChange }) => (
  <div className="alloc-sort-toggle">
    <span className="alloc-sort-label">Sort:</span>
    <button
      className={`alloc-sort-btn ${value === 'alloc' ? 'active' : ''}`}
      onClick={() => onChange('alloc')}
    >
      Allocation
    </button>
    <button
      className={`alloc-sort-btn ${value === 'return' ? 'active' : ''}`}
      onClick={() => onChange('return')}
    >
      Return
    </button>
  </div>
);

const isTouchDevice = () => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

/** Floating popover listing holdings within a sector or country group. */
const HoldingsPopover = ({ popover, onMouseEnter, onMouseLeave, onBackdropClick, hideAmounts }) => {
  if (!popover) return null;
  return (
    <>
      {isTouchDevice() && (
        <div
          className="alloc-popover-backdrop"
          aria-hidden="true"
          onClick={onBackdropClick}
        />
      )}
      <div
        className="alloc-popover"
        style={{ top: popover.top, left: popover.left }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
      <div className="alloc-popover-title">{popover.groupName}</div>
      <table className="alloc-popover-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Return</th>
            {!hideAmounts && <th>Value</th>}
          </tr>
        </thead>
        <tbody>
          {popover.items.map(h => {
            const ret = h.return_pct ?? null;
            return (
              <tr key={h.yahoo_symbol || h.t212_code}>
                <td className="pop-symbol">{h.yahoo_symbol}</td>
                <td className={ret == null ? '' : ret >= 0 ? 'color-green' : 'color-red'}>
                  {fmtPct(ret)}
                </td>
                {!hideAmounts && <td>{fmtGBP(h.market_value)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
};

/** A row for sector/country performance tables. */
const PerfRow = ({ name, renderName, allocPct, returnPct, pnl, count, maxAlloc, maxReturn, onCountEnter, onCountLeave, onCountClick, hideAmounts }) => {
  const positive = returnPct == null || returnPct >= 0;
  const allocBarPct = Math.min((allocPct / maxAlloc) * 100, 100);
  const returnBarPct = returnPct == null ? 0 : Math.min(Math.abs(returnPct) / maxReturn * 100, 100);

  return (
    <tr>
      <td className="perf-name-cell">{renderName ? renderName(name) : name}</td>
      <td className="perf-count-cell">
        <span
          className={`count-badge${count > 0 ? ' count-badge-hover' : ''}`}
          role="button"
          tabIndex={count > 0 ? 0 : -1}
          onMouseEnter={count > 0 && !isTouchDevice() ? onCountEnter : undefined}
          onMouseLeave={count > 0 && !isTouchDevice() ? onCountLeave : undefined}
          onClick={count > 0 ? onCountClick : undefined}
          onKeyDown={count > 0 && onCountClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCountClick(e); } } : undefined}
        >
          {count}
        </span>
      </td>
      <td className="perf-alloc-cell">
        <div className="perf-bar-wrap">
          <div className="perf-alloc-bar" style={{ width: `${allocBarPct}%` }} />
          <span className="perf-bar-label">{allocPct.toFixed(1)}%</span>
        </div>
      </td>
      <td className="perf-return-cell">
        <div className="perf-bar-wrap">
          <div
            className={`perf-return-bar ${positive ? 'pos' : 'neg'}`}
            style={{ width: `${returnBarPct}%` }}
          />
          <span className={`perf-bar-label ${positive ? 'color-green' : 'color-red'}`}>
            {fmtPct(returnPct)}
          </span>
        </div>
      </td>
      {!hideAmounts && (
        <td className={`perf-pnl-cell ${positive ? 'color-green' : 'color-red'}`}>
          {fmtPnl(pnl)}
        </td>
      )}
    </tr>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const TIME_RANGES = [
  { value: '1m',  label: '1M',  days: 30  },
  { value: '3m',  label: '3M',  days: 90  },
  { value: '6m',  label: '6M',  days: 180 },
  { value: '1y',  label: '1Y',  days: 365 },
  { value: 'all', label: 'All', days: null },
];

const CHART_COLORS = [
  '#6366f1', '#f59e0b', '#3b82f6', '#ec4899', '#10b981',
  '#8b5cf6', '#14b8a6', '#f97316', '#84cc16', '#06b6d4',
  '#a3a3a3', '#ef4444',
];

const Allocations = () => {
  const { hideAmounts } = useHideAmounts();
  const [chartData,        setChartData]        = useState(null);
  const [allocations,      setAllocations]      = useState(null);
  const [holdings,         setHoldings]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [timeRange,        setTimeRange]        = useState('1m');
  const [sectorSort,       setSectorSort]       = useState('alloc');
  const [countrySort,      setCountrySort]      = useState('alloc');
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [popover,          setPopover]          = useState(null);
  const [chartLoading,     setChartLoading]     = useState(false);
  const [capState,         setCapState]         = useState(null);
  const popoverTimer = useRef(null);

  useEffect(() => () => clearTimeout(popoverTimer.current), []);

  // ── Data fetching ───────────────────────────────────────────────────────────

  // Fetch static data once on mount (allocations + holdings)
  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        setLoading(true);
        // Cap state comes from /api/risk, which computes it on the same basis as
        // the agent's buy gate (value weight, ETFs exempt from thematic tags).
        // Deriving it here from raw weights would flag clusters the agent funds.
        const [allocationsData, holdingsData, riskData] = await Promise.all([
          portfolioAPI.getAllocations(),
          portfolioAPI.getCurrentHoldings(false),
          portfolioAPI.getRisk().catch(() => null),
        ]);
        setAllocations(allocationsData);
        setHoldings(holdingsData.holdings);
        setCapState(riskData?.clusters ?? null);
        setError(null);
      } catch (err) {
        setError('Failed to fetch allocation data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStaticData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch only chart/history data when time range changes
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setChartLoading(true);
        const selected = TIME_RANGES.find(r => r.value === timeRange) || TIME_RANGES[0];
        const historyData = await portfolioAPI.getHistory(selected.days);
        if (historyData.history?.length > 0) {
          const processedData = historyData.history
            .map(item => {
              const out = {
                date:     new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                fullDate: item.date,
              };
              ['country_allocation', 'sector_allocation', 'currency_allocation', 'etf_equity_split'].forEach(type => {
                const prefix = type === 'etf_equity_split' ? 'etf_' : `${type.split('_')[0]}_`;
                Object.entries(item[type] || {}).forEach(([k, v]) => { out[`${prefix}${k}`] = v; });
              });
              return out;
            })
            .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
          setChartData(processedData);
        }
      } catch (err) {
        console.error('Failed to fetch chart data:', err);
      } finally {
        setChartLoading(false);
      }
    };
    fetchChartData();
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Performance aggregations ────────────────────────────────────────────────

  const sectorPerf  = useMemo(() => groupPerformance(holdings, 'sector'),  [holdings]);
  const countryPerf = useMemo(() => groupPerformance(holdings, 'country'), [holdings]);
  const tagRows     = useMemo(
    () => groupByTag(holdings, allocations?.tag_soft_caps || {}),
    [holdings, allocations],
  );
  // tag -> buyable weight (%), from the server so this page and the agent agree.
  const capByTag = useMemo(
    () => new Map((capState || []).map((c) => [c.tag, c.gated_weight * 100])),
    [capState],
  );

  const currencyFxPnl = useMemo(() => {
    const groups = {};
    for (const h of holdings) {
      if (!h.currency) continue;
      groups[h.currency] = (groups[h.currency] || 0) + (h.fx_ppl || 0);
    }
    return groups;
  }, [holdings]);

  // Best / worst per dimension
  const summaryStats = useMemo(() => {
    const pick = (entries, fn) => entries.length ? entries.reduce(fn) : null;
    const gt   = (a, b) => b[1].returnPct > a[1].returnPct ? b : a;
    const lt   = (a, b) => b[1].returnPct < a[1].returnPct ? b : a;

    const se = Object.entries(sectorPerf ).filter(([, g]) => g.returnPct != null);
    const ce = Object.entries(countryPerf).filter(([, g]) => g.returnPct != null);

    return {
      bestSector:   pick(se, gt),
      worstSector:  pick(se, lt),
      bestCountry:  pick(ce, gt),
      worstCountry: pick(ce, lt),
    };
  }, [sectorPerf, countryPerf]);

  // ── Sorted table rows ───────────────────────────────────────────────────────

  const sectorRows = useMemo(() => {
    if (!allocations?.sector_allocation) return [];
    return Object.entries(allocations.sector_allocation)
      .map(([sector, allocPct]) => ({
        name: sector, allocPct,
        ...sectorPerf[sector],
      }))
      .sort((a, b) => sectorSort === 'return'
        ? (b.returnPct ?? -Infinity) - (a.returnPct ?? -Infinity)
        : b.allocPct - a.allocPct);
  }, [allocations, sectorPerf, sectorSort]);

  const countryRows = useMemo(() => {
    if (!allocations?.country_allocation) return [];
    return Object.entries(allocations.country_allocation)
      .map(([country, allocPct]) => ({
        name: country, allocPct,
        ...countryPerf[country],
      }))
      .sort((a, b) => countrySort === 'return'
        ? (b.returnPct ?? -Infinity) - (a.returnPct ?? -Infinity)
        : b.allocPct - a.allocPct);
  }, [allocations, countryPerf, countrySort]);

  // ── Chart helpers ───────────────────────────────────────────────────────────

  const getTopKeys = (prefix, limit = 8) => {
    if (!chartData?.length) return [];
    const latest = chartData[chartData.length - 1];
    return Object.entries(latest)
      .filter(([k, v]) => k.startsWith(prefix) && typeof v === 'number' && !isNaN(v))
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([k]) => k.replace(prefix, ''));
  };

  const topSectors    = getTopKeys('sector_');
  const topCountries  = getTopKeys('country_');
  const topCurrencies = getTopKeys('currency_');
  const etfKeys       = getTopKeys('etf_');

  // ── Popover handlers ────────────────────────────────────────────────────────

  const showPopover = (e, groupName, groupKey) => {
    clearTimeout(popoverTimer.current);
    const items = holdings
      .filter(h => h[groupKey] === groupName)
      .sort((a, b) => (b.market_value || 0) - (a.market_value || 0));
    if (!items.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const popoverWidth = Math.min(260, window.innerWidth - 24);
    const popoverMaxH = 320;
    const padding = 12;
    const isMobile = window.innerWidth <= 600;
    let left;
    let top;
    if (isMobile) {
      left = Math.max(padding, (window.innerWidth - popoverWidth) / 2);
      top = Math.max(padding, Math.min(rect.top, window.innerHeight - popoverMaxH - padding));
    } else {
      left = rect.right + padding + popoverWidth > window.innerWidth
        ? rect.left - popoverWidth - padding
        : rect.right + padding;
      top = rect.top;
      if (top + popoverMaxH > window.innerHeight - padding) {
        top = Math.max(padding, window.innerHeight - popoverMaxH - padding);
      }
    }
    setPopover({ groupName, items, top, left });
  };

  const togglePopover = (e, groupName, groupKey) => {
    if (popover?.groupName === groupName) {
      setPopover(null);
    } else {
      showPopover(e, groupName, groupKey);
    }
  };

  const scheduleHidePopover = () => {
    popoverTimer.current = setTimeout(() => setPopover(null), 150);
  };

  const cancelHidePopover = () => {
    clearTimeout(popoverTimer.current);
  };

  // ── Guard states ────────────────────────────────────────────────────────────

  if (loading) return <div className="page-fixed allocations-container"><div className="loading">Loading…</div></div>;
  if (error)   return <div className="page-fixed allocations-container"><div className="error">{error}</div></div>;
  if (!allocations) return <div className="page-fixed allocations-container"><div className="error">No allocation data available</div></div>;

  const maxSectorAlloc  = Math.max(...sectorRows.map(r => r.allocPct),  1);
  const maxCountryAlloc = Math.max(...countryRows.map(r => r.allocPct), 1);
  const maxSectorReturn  = Math.max(...sectorRows.map(r => Math.abs(r.returnPct  || 0)), 10);
  const maxCountryReturn = Math.max(...countryRows.map(r => Math.abs(r.returnPct || 0)), 10);

  const displayedCountryRows = showAllCountries
    ? countryRows
    : countryRows.filter(r => (r.count || 0) > 0);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="page-fixed allocations-container">
      <h2 className="alloc-page-title">Portfolio Allocations</h2>

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      <div className="alloc-summary-cards">
        <SummaryCard
          label="Best Sector"
          name={summaryStats.bestSector?.[0]}
          returnPct={summaryStats.bestSector?.[1]?.returnPct}
          pnl={summaryStats.bestSector?.[1]?.pnl}
          hideAmounts={hideAmounts}
        />
        <SummaryCard
          label="Weakest Sector"
          name={summaryStats.worstSector?.[0]}
          returnPct={summaryStats.worstSector?.[1]?.returnPct}
          pnl={summaryStats.worstSector?.[1]?.pnl}
          hideAmounts={hideAmounts}
        />
        <SummaryCard
          label="Best Country"
          name={summaryStats.bestCountry?.[0]}
          returnPct={summaryStats.bestCountry?.[1]?.returnPct}
          pnl={summaryStats.bestCountry?.[1]?.pnl}
          hideAmounts={hideAmounts}
        />
        <SummaryCard
          label="Weakest Country"
          name={summaryStats.worstCountry?.[0]}
          returnPct={summaryStats.worstCountry?.[1]?.returnPct}
          pnl={summaryStats.worstCountry?.[1]?.pnl}
          hideAmounts={hideAmounts}
        />
      </div>

      {/* ── Sector + Country performance tables ────────────────────────────── */}
      <div className="alloc-perf-section">

        {/* Sector */}
        <div className="alloc-perf-panel">
          <div className="alloc-perf-header">
            <h3>Sector Allocation &amp; Performance</h3>
            <SortToggle value={sectorSort} onChange={setSectorSort} />
          </div>
          <table className="perf-table">
            <thead>
              <tr>
                <th>Sector</th>
                <th title="Number of holdings">Pos</th>
                <th>Allocation</th>
                <th>Return</th>
                {!hideAmounts && <th>P&amp;L (£)</th>}
              </tr>
            </thead>
            <tbody>
              {sectorRows.map(row => (
                <PerfRow
                  key={row.name}
                  name={row.name}
                  allocPct={row.allocPct}
                  returnPct={row.returnPct ?? null}
                  pnl={row.pnl ?? null}
                  count={row.count ?? 0}
                  maxAlloc={maxSectorAlloc}
                  maxReturn={maxSectorReturn}
                  onCountEnter={(e) => showPopover(e, row.name, 'sector')}
                  onCountLeave={scheduleHidePopover}
                  onCountClick={(e) => togglePopover(e, row.name, 'sector')}
                  hideAmounts={hideAmounts}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Country */}
        <div className="alloc-perf-panel">
          <div className="alloc-perf-header">
            <h3>Country Allocation &amp; Performance</h3>
            <SortToggle value={countrySort} onChange={setCountrySort} />
          </div>
          <table className="perf-table">
            <thead>
              <tr>
                <th>Country</th>
                <th title="Number of holdings">Pos</th>
                <th>Allocation</th>
                <th>Return</th>
                {!hideAmounts && <th>P&amp;L (£)</th>}
              </tr>
            </thead>
            <tbody>
              {displayedCountryRows.map(row => (
                <PerfRow
                  key={row.name}
                  name={row.name}
                  renderName={renderCountryWithFlag}
                  allocPct={row.allocPct}
                  returnPct={row.returnPct ?? null}
                  pnl={row.pnl ?? null}
                  count={row.count ?? 0}
                  maxAlloc={maxCountryAlloc}
                  maxReturn={maxCountryReturn}
                  onCountEnter={(e) => showPopover(e, row.name, 'country')}
                  onCountLeave={scheduleHidePopover}
                  onCountClick={(e) => togglePopover(e, row.name, 'country')}
                  hideAmounts={hideAmounts}
                />
              ))}
            </tbody>
          </table>
          {(() => {
            const zeroCount = countryRows.filter(r => (r.count || 0) === 0).length;
            return zeroCount > 0 && (
              <button
                className="alloc-show-more-btn"
                onClick={() => setShowAllCountries(v => !v)}
              >
                {showAllCountries
                  ? 'Hide zero-position countries'
                  : `Show ${zeroCount} more (ETF domiciles)`}
              </button>
            );
          })()}
        </div>
      </div>

      {/* ── Theme exposure (tags) vs soft caps ─────────────────────────────── */}
      {tagRows.length > 0 && (
        <div className="alloc-perf-panel tag-cluster-panel">
          <div className="alloc-perf-header">
            <h3>Theme Exposure</h3>
            <span className="tag-cluster-note">
              tags overlap — rows don&apos;t sum to 100% · caps are advisory review triggers
            </span>
          </div>
          <table className="perf-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th title="Number of holdings">Pos</th>
                <th title="Bar shows how full the risk budget is (100% = at cap)">Weight</th>
                <th>Soft cap</th>
                <th>Return</th>
                {!hideAmounts && <th>P&amp;L (£)</th>}
              </tr>
            </thead>
            <tbody>
              {tagRows.map(row => {
                // Buyable weight excludes ETFs from thematic clusters, so it can sit
                // well below the displayed weight; null when /api/risk is unavailable,
                // in which case no flag is shown rather than one on the wrong basis.
                const gated = capByTag.get(row.tag);
                const over = gated != null && row.capPct != null && gated >= row.capPct;
                const near = gated != null && row.capPct != null && !over && gated > row.capPct * 0.85;
                // Fill toward the cap on the same weight the flag tests, or the bar
                // reads full while the badge says there is headroom.
                const barPct = row.capPct && gated != null
                  ? Math.min(100, (gated / row.capPct) * 100)
                  : Math.min(100, row.weightPct);
                const showGated = gated != null && Math.abs(gated - row.weightPct) > 0.05;
                return (
                  <tr key={row.tag} className={over ? 'tag-over-cap' : near ? 'tag-near-cap' : ''}>
                    <td className="tag-name">{row.tag}</td>
                    <td>{row.count}</td>
                    <td>
                      <div
                        className="tag-weight-cell"
                        title={
                          showGated
                            ? 'ETFs are diversified by construction, so they are exempt from thematic caps'
                            : undefined
                        }
                      >
                        <div className="tag-weight-bar-track">
                          <div
                            className={`tag-weight-bar${over ? ' over' : near ? ' near' : ''}`}
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                        <span>
                          {row.weightPct.toFixed(1)}%
                          {/* Named because the bar tracks this, not the held weight. */}
                          {showGated && (
                            <span className="tag-weight-gated"> · {gated.toFixed(1)}% buyable</span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td>
                      {row.capPct != null ? `${row.capPct}%` : '—'}
                      {over && <span className="tag-cap-flag">over</span>}
                    </td>
                    <td className={row.returnPct == null ? '' : row.returnPct >= 0 ? 'positive' : 'negative'}>
                      {fmtPct(row.returnPct)}
                    </td>
                    {!hideAmounts && (
                      <td className={row.pnl >= 0 ? 'positive' : 'negative'}>{fmtPnl(row.pnl)}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Currency + ETF/Equity ───────────────────────────────────────────── */}
      <div className="alloc-secondary-section">

        <div className="allocation-table">
          <h3>Currency Allocation</h3>
          <table>
            <thead>
              <tr>
                <th>Currency</th>
                <th>Allocation</th>
                {!hideAmounts && (
                  <th title="FX contribution to P&L in GBP. Negative = strong GBP hurt returns.">FX P&amp;L</th>
                )}
              </tr>
            </thead>
            <tbody>
              {allocations.currency_allocation && Object.entries(allocations.currency_allocation)
                .sort(([, a], [, b]) => b - a)
                .map(([currency, pct]) => {
                  const fx = currencyFxPnl[currency];
                  const isHome = currency === 'GBP';
                  const fxDisplay = isHome ? '—' : (fx != null ? fmtPnl(fx) : '—');
                  const fxPositive = isHome || fx == null || fx >= 0;
                  return (
                    <tr key={currency}>
                      <td>
                        <span className="currency-code">{currency}</span>
                        {currency === 'USD' && ' 🇺🇸'}
                        {currency === 'GBP' && ' 🇬🇧'}
                        {currency === 'EUR' && ' 🇪🇺'}
                        {currency === 'CAD' && ' 🇨🇦'}
                        {currency === 'JPY' && ' 🇯🇵'}
                      </td>
                      <td style={{ '--bar-width': `${Math.min(pct, 100)}%` }}>
                        <span>{pct.toFixed(2)}%</span>
                      </td>
                      {!hideAmounts && (
                        <td className={fxPositive ? 'color-green' : 'color-red'}>
                          {fxDisplay}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="allocation-table">
          <h3>ETF / Equity Split</h3>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Allocation</th>
                {!hideAmounts && <th>Value (£)</th>}
              </tr>
            </thead>
            <tbody>
              {allocations.etf_equity_split && Object.entries(allocations.etf_equity_split)
                .sort(([, a], [, b]) => b - a)
                .map(([type, pct]) => (
                  <tr key={type}>
                    <td>{type}</td>
                    <td style={{ '--bar-width': `${Math.min(pct, 100)}%` }}>
                      <span>{pct.toFixed(2)}%</span>
                    </td>
                    {!hideAmounts && (
                      <td>{fmtGBP(allocations.total_value * pct / 100)}</td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Time-series charts ──────────────────────────────────────────────── */}
      {chartData?.length > 0 && (
        <div className={`alloc-charts-section${chartLoading ? ' alloc-charts-loading' : ''}`}>
          <div className="alloc-charts-header">
            <h3>Allocation Over Time</h3>
            <div className="time-range-selector">
              {TIME_RANGES.map(r => (
                <button
                  key={r.value}
                  className={`time-range-btn ${timeRange === r.value ? 'active' : ''}`}
                  onClick={() => setTimeRange(r.value)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="allocation-charts">
            {[
              { title: 'Sector',   keys: topSectors,    prefix: 'sector_'   },
              { title: 'Country',  keys: topCountries,  prefix: 'country_'  },
              { title: 'Currency', keys: topCurrencies, prefix: 'currency_' },
              { title: 'ETF vs Equity', keys: etfKeys,  prefix: 'etf_'      },
            ].map(({ title, keys, prefix }) => (
              <div key={title} className="chart-panel allocation-panel">
                <h3>{title}</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={<SharedTooltip prefix={prefix} valueFormatter={v => `${v.toFixed(1)}%`} />}
                      wrapperStyle={{ zIndex: 9999, maxWidth: 'min(280px, calc(100vw - 24px))' }}
                      contentStyle={{ pointerEvents: 'none' }}
                    />
                    <Legend />
                    {keys.map((k, i) => (
                      // Unanimated per PortfolioChart: Recharts 3 + React 19 leaves
                      // marks at zero extent when the mount animation doesn't fire.
                      <Line
                        key={k}
                        type="monotone"
                        dataKey={`${prefix}${k}`}
                        name={k}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </div>
      )}

      <HoldingsPopover
        popover={popover}
        onMouseEnter={cancelHidePopover}
        onMouseLeave={scheduleHidePopover}
        onBackdropClick={() => setPopover(null)}
        hideAmounts={hideAmounts}
      />
    </div>
  );
};

export default Allocations;
